<template>
  <div>
    <v-dialog
      v-model="dialog"
      @click:outside="onBack"
      content-class="insp-dialog-class"
    >
      <v-card-title class="close-btn-container d-flex justify-end">
        <v-btn class="close-btn" icon @click="onBack">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <ReadinessCheckListView
        :proficiencyLevel="currentData?.data?.importanceLevel"
        :badge="currentData?.data?.badge"
        :responsibility="{
          id: currentData?.data?.responsibility_id,
          code: extractCode(currentData?.data?.code),
          name: currentData?.data?.name,
          type: currentData?.data?.type,
        }"
        :checkPoints="currentData?.checkpointsData"
        :checkpointCounts="currentData?.data?.checkpointCounts"
        :availableInspectors="currentData?.data?.avatars"
        :selectedItem="selectedItemTab"
      />
    </v-dialog>
    <div>
      <div v-if="!loading.initial && dataSource.length === 0">
        <NoRecordFound
          :title="''"
          :subtitle="''"
          showSpecificMessage="You're Not Assigned to a Position Yet"
          message=""
          class="no-record-found"
          :shouldShowAddAction="false"
          :noBorder="true"
          :showActiveCounter="false"
          :showClearAction="false"
          btnTitle=""
          description="Your readiness progress is linked to a position. Once assigned, you'll see your positions, roles and responsibilities."
        />
      </div>
      <div v-if="!loading.initial && dataSource.length > 0">
        <div class="employee-path">
          <PathToReadinessTreeList
            @optionClick="onOptionClick"
            @avatarOptionClick="onAvatarOptionClick"
            :dataSource="dataSource"
            :topLevel="topLevel"
            :removeBorder="removeBorder"
            :dropdownItems="dropdownItems"
            :pathToReadinessColumnName="'Assignments'"
            :showInspectionBadge="true"
            :showDropDown="false"
            :showRespBadge="true"
            :showAvailableCoaches="true"
            :showImportanceSelector="false"
            :myStuffTreeMode="true"
            :autoExpandAll="false"
            :showInspectionButtons="false"
            @treeExpandRole="onTreeExpandRole"
            :expandedKeys="expandedKeys"
            @onExpansionStateChange="handleExpansionStateChange"
          />
          <ExPagination
            v-if="
              dataSource.length > 0 && pagination.total > pagination.perPage
            "
            :pagination="pagination"
            @onRequest="handlePaginationRequest"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import PathToReadinessTreeList from "@components/DataTable/PathToReadinessTreeList.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import ReadinessCheckListView from "../views/ReadinessCheckListView/ReadinessCheckListView.vue";
import configService from "../services/config";
import Knock, { KnockGuideClient } from "@knocklabs/client";
import {
  getEmployeeAssignments,
  recalculateEmployeeReadiness,
  getListActiveCheckpointsEval,
  getRoleAssignment,
  getResponsibilityAssignment,
} from "@/api";
export default {
  components: {
    PathToReadinessTreeList,
    ReadinessCheckListView,
    NoRecordFound,
    ExPagination,
  },
  async mounted() {
    this.$loader.show();

    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    this.authUser = JSON.parse(localStorage.getItem("user"));
    await this.initializeKnock();
    this.clearModalStates();

    try {
      await this.getEmployeesAssignments();
      await this.getAuthUserReadiness();
    } catch (error) {
      console.error("Error during component initialization:", error);
    } finally {
      this.loading.initial = false;
      this.$loader.hide();
    }
  },
  computed: {
    refactoredAsEmployeePage() {
      return this.$store.getters["featureFlags/ideaMDP378"];
    },
  },
  data() {
    return {
      menuItems: [{ label: "Your Path to Readiness", enabled: true }],
      dataSource: [],
      accountID: null,
      topLevel: "position",
      removeBorder: false,
      allResponsibilities: [],
      isForceBadge: false,
      showEmployeeIssueBadge: false,
      currentResponsibility: null,
      isInspectResModal: false,
      checkpointsData: [],
      evalModalEmpState: null,
      evalModalHeaderState: null,
      loading: {
        initial: true,
        readinessBar: false,
        responsibilities: {},
        assignments: false,
        forceBadge: false,
        employeeStatus: false,
        checkpoint: false,
        pagination: false,
        modal: false,
      },
      dialog: false,
      currentData: null,
      selectedItemTab: null,
      selectedTab: 0,
      currentReadiness: null,
      pagination: {
        limit: 10,
        offset: 0,
        total: 0,
        perPage: 10,
        page: 1,
      },
      // Add expanded row loading states
      expandedRowLoadingStates: {},
      // Store expanded tree state
      expandedKeys: [],
      // Store loaded child data to prevent duplicate API calls during expansion
      loadedChildData: new Map(),
      knockClient: null,
      guideClient: null,
      guideData: null,
      guides: [],
      knock: null,
    };
  },
  mixins: [dropdownMixin],
  beforeDestroy() {
    // Clean up modal states when component is destroyed
    this.clearModalStates();
  },
  methods: {
    async getEmployeesAssignments() {
      try {
        this.loading.assignments = true;
        const empId = this.authUser?.employee?.id;
        const { data } = await getEmployeeAssignments(
          this.accountID,
          empId,
          this.pagination.limit,
          this.pagination.offset,
          "active"
        );

        if (data) {
          // Update pagination data
          this.pagination.total = data.count || 0;

          if (data.results?.length > 0) {
            this.dataSource = this.transformData(data.results);
          } else {
            this.dataSource = [];
          }
        } else {
          this.dataSource = [];
          this.pagination.total = 0;
        }
      } catch (error) {
        console.error("Error fetching employee assignments:", error);
        this.dataSource = [];
        this.pagination.total = 0;
      } finally {
        this.loading.assignments = false;
      }
    },

    async getAuthUserReadiness() {
      try {
        this.loading.readinessBar = true;
        const empId = this.authUser?.employee?.id;
        const val = false;
        const { data } = await recalculateEmployeeReadiness(
          this.accountID,
          empId,
          val
        );
        this.currentReadiness = data;
      } catch (error) {
        console.error("Error fetching employee readiness:", error);
        this.currentReadiness = null;
      } finally {
        this.loading.readinessBar = false;
      }
    },

    async fetchResponsibilityCheckpoints(responsibility) {
      const empId = this.authUser?.employee?.id;
      try {
        this.loading.checkpoint = true;
        let checkpointsData = await this.loadAllCheckpointsEvaluationData(
          this.accountID,
          empId,
          responsibility.responsibility_id
        );

        if (checkpointsData && checkpointsData.length > 0) {
          checkpointsData = checkpointsData.map((el) => ({
            id: el.id,
            evaluation_status: el.evaluation_status,
            proficiency_level: el.proficiency_level,
            status: el.status,
            responsibility: el.responsibility,
          }));

          return checkpointsData;
        } else {
          return [];
        }
      } catch (error) {
        console.error(
          `Error loading checkpoints for responsibility ${responsibility.responsibility_id}:`,
          error
        );
        return [];
      } finally {
        this.loading.checkpoint = false;
      }
    },

    async loadAllCheckpointsEvaluationData(
      accountId,
      employeeId,
      responsibilityId
    ) {
      try {
        const response = await getListActiveCheckpointsEval(
          accountId,
          responsibilityId,
          employeeId
        );
        return response.data;
      } catch (error) {
        console.error("Error loading checkpoints evaluation data:", error);
        return [];
      }
    },

    transformData(dataArray) {
      if (!Array.isArray(dataArray)) {
        console.warn("transformData received non-array data:", dataArray);
        return [];
      }

      const result = [];
      const today = new Date();

      dataArray.forEach((data) => {
        if (!data || !data.id) {
          console.warn("Invalid data item in transformData:", data);
          return;
        }

        // Transform position data
        const positionObject = this.transformPositionData(data, today);
        result.push(positionObject);

        // TODO: Later - handle role assignments when API is added
        // if (data.role_assignment_count > 0) {
        //   // Will call roles API and process role_assignments
        // }
      });

      // Sort the results by commitment level
      const commitmentOrder = {
        Primary: 1,
        "Preparing for": 2,
        Backup: 3,
      };

      result.sort((a, b) => {
        const aCommit = a.data?.commitmentLevel || "";
        const bCommit = b.data?.commitmentLevel || "";
        return (
          (commitmentOrder[aCommit] || 99) - (commitmentOrder[bCommit] || 99)
        );
      });

      return result;
    },

    transformPositionData(data, today) {
      const isNew =
        data.assigned_on &&
        new Date(data.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 >
          today.getTime();

      return {
        id: `position_${data.id}`, // Make position IDs unique
        parentId: -1, // Root-level has no parent
        data: {
          type: data.assignment_type || "position",
          commitmentLevel: data.commitment_level || "",
          assignmentType: data.assignment_type || "position",
          importanceLevel: data.importance || 0,
          importanceSelector: true,
          totalSteps: 3,
          code: data.position_code || "",
          name: data.position_name || "",
          isNew: isNew || false,
          isAccepted: false,
          acceptanceStatus: "pending",
          // Add expandable indicator if roles exist
          hasRoles: data.role_assignment_count > 0,
          roleCount: data.role_assignment_count,
        },
        assignment_id: data.id,
        status: data.status || "active",
        readiness: data.current_readiness || 0,
      };
    },

    extractCode(str) {
      try {
        if (!str || typeof str !== "string") {
          return null;
        }
        let match = str.match(/#\d+/);
        return match ? match[0] : null;
      } catch (error) {
        console.error("Error extracting code from string:", str, error);
        return null;
      }
    },

    dropdownItems() {
      const items = [
        {
          name: "Activate",
          value: "activate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Delete",
          value: "delete_action",
          icon: "icons/dropdown/delete.svg",
          negative: true,
          order: 3,
        },
      ];
      return items;
    },

    async onOptionClick(data) {
      try {
        if (!data || !data.data) {
          console.warn("Invalid data passed to onOptionClick:", data);
          return;
        }

        this.loading.modal = true;
        this.currentData = data.data;
        this.dialog = true;
        this.selectedItemTab = 0;
      } catch (error) {
        console.error("Error in onOptionClick:", error);
        this.clearModalStates();
      } finally {
        this.loading.modal = false;
      }
    },

    async onAvatarOptionClick(data) {
      try {
        if (!data || !data.data) {
          console.warn("Invalid data passed to onAvatarOptionClick:", data);
          return;
        }

        // Hide all active tooltips before opening modal
        this.hideAllTooltips();

        this.loading.modal = true;
        this.currentData = data.data;
        this.dialog = true;
        this.selectedItemTab = 1;
      } catch (error) {
        console.error("Error in onAvatarOptionClick:", error);
        this.clearModalStates();
      } finally {
        this.loading.modal = false;
      }
    },

    onBack() {
      try {
        this.dialog = false;
        this.selectedItemTab = null;
        this.clearModalStates();

        // Hide any lingering tooltips after modal closes and prevent immediate re-showing
        this.$nextTick(() => {
          this.hideAllTooltips();
          this.temporarilyDisableTooltips();
        });
      } catch (error) {
        console.error("Error in onBack:", error);
        // Ensure modal states are cleared even if there's an error
        this.dialog = false;
        this.selectedItemTab = null;
        this.currentData = null;
        this.hideAllTooltips();
        this.temporarilyDisableTooltips();
      }
    },

    clearModalStates() {
      // Clear all modal states to prevent unwanted reopening
      this.dialog = false;
      this.selectedItemTab = null;
      this.currentData = null;
      this.checkpointsData = [];
      this.evalModalEmpState = null;
      this.evalModalHeaderState = null;
      this.currentResponsibility = null;
    },

    hideAllTooltips() {
      // Force close all Element UI tooltips by removing their DOM elements
      try {
        const tooltips = document.querySelectorAll(".el-tooltip__popper");
        tooltips.forEach((tooltip) => {
          tooltip.style.display = "none";
        });

        // Trigger mouseleave on all tooltip trigger elements (avatars, buttons, etc.)
        const tooltipTriggers = document.querySelectorAll(".el-tooltip");
        tooltipTriggers.forEach((trigger) => {
          const event = new MouseEvent("mouseleave", {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          trigger.dispatchEvent(event);
        });

        // Also dispatch on the entire document to catch any remaining tooltips
        const bodyEvent = new MouseEvent("mouseleave", {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        document.body.dispatchEvent(bodyEvent);
      } catch (error) {
        console.error("Error hiding tooltips:", error);
      }
    },

    temporarilyDisableTooltips() {
      // Temporarily disable pointer events on avatar containers to prevent tooltip re-showing
      try {
        // Find all avatar containers in the Available Inspectors column
        const avatarContainers = document.querySelectorAll(
          ".people-button, .no-hover-people-button"
        );

        // Store original pointer events values
        const originalPointerEvents = [];

        avatarContainers.forEach((container, index) => {
          originalPointerEvents[index] = container.style.pointerEvents;
          container.style.pointerEvents = "none";
        });

        // Re-enable pointer events after 500ms (enough time for user to move mouse)
        setTimeout(() => {
          avatarContainers.forEach((container, index) => {
            container.style.pointerEvents = originalPointerEvents[index] || "";
          });
        }, 500);
      } catch (error) {
        console.error("Error temporarily disabling tooltips:", error);
      }
    },

    async handlePaginationRequest(paginationData) {
      // Calculate offset from page and limit [[memory:5237252]]
      this.pagination.page = paginationData.page;
      this.pagination.offset =
        (paginationData.page - 1) * this.pagination.limit;

      try {
        this.loading.pagination = true;
        this.$loader.show();
        await this.getEmployeesAssignments();
      } catch (error) {
        console.error("Error loading paginated data:", error);
      } finally {
        this.loading.pagination = false;
        this.$loader.hide();
      }
    },

    async onTreeExpandRole({ assignmentId, rowType, expandedRowData }) {
      const accountID = this.accountID;
      const empId = this.authUser?.employee?.id;

      // Check if data is already loaded for this expansion
      const cacheKey = `${rowType}_${assignmentId}`;
      if (this.loadedChildData.has(cacheKey)) {
        console.log(`Data already loaded for ${cacheKey}, skipping API call`);
        return;
      }

      this.removeSkeletonLoaders(assignmentId);

      try {
        if (rowType === "position") {
          await this.handlePositionExpansion(accountID, empId, assignmentId);
          // Mark this position's roles as loaded
          this.loadedChildData.set(cacheKey, true);
        } else if (rowType === "role") {
          await this.handleRoleExpansion(
            accountID,
            empId,
            assignmentId,
            expandedRowData
          );
          // Mark this role's responsibilities as loaded
          this.loadedChildData.set(cacheKey, true);
        }
      } catch (error) {
        console.error("Error loading assignments:", error);
        this.removeSkeletonLoaders(assignmentId);
      }
    },

    async handlePositionExpansion(accountID, empId, positionAssignmentId) {
      const existingRoles = this.dataSource.filter(
        (item) =>
          item.parentId === `position_${positionAssignmentId}` &&
          item.data?.type === "role" &&
          !item.data?.isSkeletonLoader
      );

      if (existingRoles.length > 0) {
        console.log(`Roles already exist for position ${positionAssignmentId}`);
        return;
      }

      const positionData = this.dataSource.find(
        (item) => item.id === `position_${positionAssignmentId}`
      );
      const roleCount = positionData?.data?.roleCount || 2; // fallback to 2 if not found

      this.addSkeletonLoaders(
        `position_${positionAssignmentId}`,
        "position",
        roleCount
      );

      const response = await getRoleAssignment(
        accountID,
        empId,
        positionAssignmentId,
        "active"
      );

      this.removeSkeletonLoaders(`position_${positionAssignmentId}`);

      if (
        response.data &&
        response.data.results &&
        response.data.results.length > 0
      ) {
        const today = new Date();
        const roleData = response.data.results;

        const transformedRoles = roleData.map((role) =>
          this.transformRoleData(
            role,
            `position_${positionAssignmentId}`,
            today
          )
        );

        this.dataSource.push(...transformedRoles);

        const positionIndex = this.dataSource.findIndex(
          (item) => item.id === `position_${positionAssignmentId}`
        );
        if (positionIndex !== -1) {
          this.dataSource[positionIndex].data.hasRoles = true;
          this.dataSource[positionIndex].data.roleCount =
            transformedRoles.length;
        }
      }
    },

    async handleRoleExpansion(
      accountID,
      empId,
      roleAssignmentId,
      expandedRowData
    ) {
      const existingResponsibilities = this.dataSource.filter(
        (item) =>
          item.parentId === expandedRowData.id &&
          item.data?.type === "responsibility" &&
          !item.data?.isSkeletonLoader
      );

      if (existingResponsibilities.length > 0) {
        console.log(
          `Responsibilities already exist for role ${roleAssignmentId}`
        );
        return;
      }

      const responsibilityCount =
        expandedRowData.data?.responsibilityCount || 3; // fallback to 3 if not found

      this.addSkeletonLoaders(expandedRowData.id, "role", responsibilityCount);

      const response = await getResponsibilityAssignment(
        accountID,
        roleAssignmentId,
        "active",
        "active"
      );

      this.removeSkeletonLoaders(expandedRowData.id);

      if (
        response.data &&
        response.data.results &&
        response.data.results.length > 0
      ) {
        const today = new Date();
        const responsibilityData = response.data.results;

        const transformedResponsibilities = responsibilityData.map((resp) =>
          this.transformResponsibilityDataFromAPI(
            resp,
            expandedRowData.id,
            roleAssignmentId,
            today
          )
        );

        this.dataSource.push(...transformedResponsibilities);

        // Update the parent role to show it has responsibilities
        const roleIndex = this.dataSource.findIndex(
          (item) => item.id === expandedRowData.id
        );
        if (roleIndex !== -1) {
          this.dataSource[roleIndex].data.hasResponsibilities = true;
          this.dataSource[roleIndex].data.responsibilityCount =
            transformedResponsibilities.length;
        }
      }
    },

    transformRoleData(role, parentId, today) {
      const isRoleNew =
        role.assigned_on &&
        new Date(role.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 >
          today.getTime();

      return {
        id: `role_${role.id}`, // Make role IDs unique
        parentId: parentId, // Set parent ID to main object
        data: {
          type: role.assignment_type || "role",
          commitmentLevel: role.commitment_level || "",
          assignmentType: role.assignment_type || "role",
          importanceLevel: role.importance || 0,
          importanceSelector: true,
          totalSteps: 3,
          code: role.role_code || "",
          name: role.role_name || "",
          isNew: isRoleNew || false,
          isAccepted: isRoleNew || false,
          acceptanceStatus: "pending",
          assignment_id: parentId,
          // Add expandable indicator if responsibilities exist
          hasResponsibilities: role.responsibility_assignment_count > 0,
          responsibilityCount: role.responsibility_assignment_count,
        },
        assignment_id: role.id,
        readiness: role.current_readiness || 0,
      };
    },

    transformResponsibilityDataFromAPI(resp, parentId, assignmentId, today) {
      const checkpoints = [];
      const isRespNew =
        resp.assigned_on &&
        new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 >
          today.getTime();

      let badge = null;
      if (resp.badge_level !== null && resp.badge_status !== "expired") {
        badge = {
          id: resp.badge_id,
          level: resp.badge_level,
          status: resp.badge_issued_method,
          expiration_date: resp.badge_expiration_date,
          issued_method: resp.badge_issued_method,
        };
      }

      // Extract checkpoint counts from API response
      const checkpointCounts = {
        apprentice_checkpoint_count: resp.apprentice_checkpoint_count || 0,
        professional_checkpoint_count: resp.professional_checkpoint_count || 0,
        coach_checkpoint_count: resp.coach_checkpoint_count || 0,
        master_checkpoint_count: resp.master_checkpoint_count || 0,
        apprentice_passed_checkpoint_count:
          resp.apprentice_passed_checkpoint_count || 0,
        professional_passed_checkpoint_count:
          resp.professional_passed_checkpoint_count || 0,
        coach_passed_checkpoint_count: resp.coach_passed_checkpoint_count || 0,
        master_passed_checkpoint_count:
          resp.master_passed_checkpoint_count || 0,
      };

      return {
        id: `responsibility_${resp.id}`, // Make responsibility IDs unique
        parentId: parentId, // Correctly set parent ID
        data: {
          type: resp.assignment_type || "responsibility",
          commitmentLevel: resp.commitment_level || "",
          assignmentType: resp.assignment_type || "responsibility",
          importanceLevel: resp.importance || 0,
          importanceSelector: true,
          totalSteps: 3,
          code: resp.responsibility_code || "",
          name: resp.responsibility_name || "",
          isNew: isRespNew || false,
          isAccepted: isRespNew || false,
          acceptanceStatus: "pending",
          assignment_id: assignmentId,
          badge: badge,
          responsibility_id: resp.responsibility,
          checkpointCounts: checkpointCounts,
          avatars: (resp.available_inspectors || []).map((inspector) => {
            // Split full_name into firstName and lastName
            const nameParts = (inspector.full_name || "").trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            // Try to find badge level from badge_holders by matching name
            const badgeHolder = (resp.badge_holders || []).find((bh) => {
              const badgeHolderFullName = `${bh.first_name || ""} ${
                bh.last_name || ""
              }`.trim();
              return badgeHolderFullName === (inspector.full_name || "").trim();
            });

            return {
              id: inspector.id,
              badgeLevel: badgeHolder?.badge_level || null,
              firstName: firstName,
              lastName: lastName,
              fullName: inspector.full_name || "",
              title: badgeHolder?.title || null,
              master_will_coach: badgeHolder?.master_will_coach || null,
              img: inspector.avatar || "",
              isHoverEnabled: !!inspector.avatar,
            };
          }),
        },
        checkpointsData: checkpoints,
        status: resp.status || "active",
        readiness: resp.current_readiness || 0,
      };
    },

    addSkeletonLoaders(parentId, rowType, count = null) {
      // Check if skeleton loaders already exist for this parent
      const existingSkeletons = this.dataSource.filter(
        (item) => item.parentId === parentId && item.data?.isSkeletonLoader
      );

      if (existingSkeletons.length > 0) {
        return; // Skip adding if already exists
      }

      // Use provided count or fallback to default values
      const skeletonCount = count || (rowType === "position" ? 2 : 3);
      const skeletonItems = [];

      for (let i = 0; i < skeletonCount; i++) {
        const skeletonId = `skeleton-${parentId}-${i}`;
        skeletonItems.push({
          id: skeletonId,
          parentId: parentId,
          data: {
            type: "skeleton",
            assignmentType: rowType === "position" ? "role" : "responsibility",
            skeletonFor: rowType,
            isSkeletonLoader: true,
            commitmentLevel: "",
            importanceLevel: 0,
            importanceSelector: false,
            totalSteps: 3,
            code: "",
            name: "",
            isNew: false,
            isAccepted: false,
            acceptanceStatus: "pending",
          },
          readiness: 0,
          status: "loading",
        });
      }

      // Add skeleton items to dataSource
      this.dataSource = [...this.dataSource, ...skeletonItems];
    },

    removeSkeletonLoaders(parentId) {
      // Remove skeleton items for this parent
      this.dataSource = this.dataSource.filter((item) => {
        const isSkeletonForParent = item.id
          .toString()
          .startsWith(`skeleton-${parentId}-`);
        const isSkeletonLoader = item.data?.isSkeletonLoader;
        const hasCorrectParent = item.parentId === parentId;

        // Remove if it's a skeleton loader for this parent
        return !(isSkeletonForParent && isSkeletonLoader && hasCorrectParent);
      });
    },

    handleExpansionStateChange(expandedKeys) {
      // Update our local copy of expanded keys when user manually expands/collapses
      this.expandedKeys = expandedKeys || [];
    },

    handleTabSelected(tab) {
      const tabIndexMap = {
        "Your Path to Readiness": 0,
      };

      this.selectedTab = tabIndexMap[tab] || 0;
    },

    async initializeKnock() {
      try {
        const apiKey = configService.get("knockApiKey");
        const userId = this.authUser?.id?.toString();
        const channelId = configService.get("knockGuideChannelId");

        this.knockClient = new Knock(apiKey);
        this.knockClient.authenticate({ id: userId });

        this.guideClient = new KnockGuideClient(this.knockClient, channelId);

        this.guideClient.store.subscribe(() => {
          const storeState = this.guideClient.store.state;

          this.guides = this.guideClient.selectGuides(storeState) || [];
          const welcomeGuide = this.guides.find(
            (guide) => guide.key === "welcome-as-employee"
          );
          this.guideData = welcomeGuide?.steps?.[0] || null;
        });

        await this.guideClient.subscribe();
        await this.guideClient.fetch();
      } catch (error) {
        console.error("❌ Error initializing Knock:", error);
      }
    },

    handleGuideDismiss() {},
  },
};
</script>

<style lang="css" scoped>
.readiness-container {
  margin: 15px;
  padding-top: 15px;
  width: 100%;
}

.employee-path {
  border-radius: 8px;
}
::v-deep .progress-bar-container {
  width: 100%;
}
.employee-readiness-container {
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow: none;
  box-shadow: 0px 1px 3px 0px rgba(16, 24, 40, 0.1),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

#treelist {
  max-width: 100%;
}

::v-deep(.v-tabs) {
  padding: 13px 0px !important;
  border-bottom: 1px solid #eaecf0 !important;
}

::v-deep(.custom-tabs) {
  top: 0px;
}

::v-deep(.container) {
  padding: 0px !important;
}

.close-btn-container {
  position: absolute;
  right: 25px;
  margin-top: 10px;
}

.close-btn {
  position: absolute;
}

::v-deep(.empty-background-pattern) {
  padding-top: 0px !important;
}

::v-deep(.table-content > div:first-child) {
  margin-bottom: 4px !important;
}
::v-deep .employee-readiness .new-progress-bar-container {
  margin-left: 5px;
}
</style>
